import { zodResolver } from '@hookform/resolvers/zod'
import type { ModuleForStudent } from '@viastud/server/routers/user_router'
import { Checkbox } from '@viastud/ui/checkbox'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { trpc } from '@viastud/ui/lib/trpc'
import type { UserDto } from '@viastud/ui/shared/edit-profile'
import { EditProfile } from '@viastud/ui/shared/edit-profile'
import type { EditProfileSchema, Grade, Subject } from '@viastud/utils'
import { editProfileSchema, subject, SubjectEnum } from '@viastud/utils'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import type { UserAuthStateAuthenticated } from '@/store/auth.store'

interface StudentSettingsProps {
  student: UserDto
  updateAuth?: (auth: UserAuthStateAuthenticated) => void
  editPassword?: boolean
  isDirty: boolean
  setIsDirty: Dispatch<SetStateAction<boolean>>
  onSuccess?: () => void
}

export function StudentSettings({
  student,
  updateAuth,
  editPassword,
  isDirty,
  setIsDirty,
  onSuccess,
}: StudentSettingsProps) {
  const [checkedGrade, setCheckedGrade] = useState<Grade | null>(null)
  const [interestedIn, setInterestedIn] = useState<Subject[]>([])
  const { toast } = useToast()
  const { data: studentData } = trpc.user.getStudentDetails.useQuery({
    id: student.id,
  })
  const { data: childrenTokenInfo } = trpc.user.getChildrenTokenBalance.useQuery({
    userId: student.id,
  })

  const [studentModules, setStudentModules] = useState<ModuleForStudent[]>([])

  useEffect(() => {
    if (studentData?.modules) {
      // Garder tous les modules pour préserver l'état, ne pas filtrer ici
      setStudentModules(
        studentData.modules.map((module) => ({
          ...module,
          chapter: { ...module.chapter, imageUrl: null },
        }))
      )
    }
  }, [studentData])

  useEffect(() => {
    if (studentData) {
      setCheckedGrade(studentData.grade ?? null)
      setInterestedIn(studentData.interestedIn)
    }
  }, [studentData])

  const editProfileForm = useForm<EditProfileSchema>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      id: student?.id ?? '',
      lastName: student?.lastName ?? '',
      firstName: student?.firstName ?? '',
      email: student?.email ?? '',
      phoneNumber: student?.phoneNumber ?? '',
    },
  })

  const initialFirstName = student?.firstName ?? ''
  const initialLastName = student?.lastName ?? ''

  const firstName = useWatch({
    control: editProfileForm.control,
    name: 'firstName',
  })

  const lastName = useWatch({
    control: editProfileForm.control,
    name: 'lastName',
  })

  useEffect(() => {
    const hasChanged = firstName !== initialFirstName || lastName !== initialLastName
    setIsDirty(hasChanged)
  }, [setIsDirty, firstName, lastName, initialFirstName, initialLastName])

  const { mutateAsync: editProfileMutation } = trpc.user.edit.useMutation({
    onSuccess: (data) => {
      // Si updateAuth est fourni, on l'utilise (contexte étudiant)
      // Sinon, on ne fait rien (contexte parent - on ne veut pas changer l'auth du parent)
      if (updateAuth) {
        updateAuth({ user: data, role: 'STUDENT', isAuthenticated: true })
      }
      // Appeler le callback onSuccess si fourni
      if (onSuccess) {
        onSuccess()
      }
      toast({
        title: 'Profil modifié avec succès',
      })
      setIsDirty(false)
    },
    onError: () => {
      toast({
        title: 'Erreur lors de la modification du profil',
      })
    },
  })

  const changePasswordMutation = trpc.userAuth.changePassword.useMutation()
  const onSubmit = async (data: EditProfileSchema) => {
    // On ne garde que les modules qui ont été cochés (doneModule ou doingModule à true)
    const modulesToSend = studentModules.filter((module) => module.doneModule || module.doingModule)
    await editProfileMutation({
      ...data,
      interestedIn,
      grade: checkedGrade,
      modules: modulesToSend,
    })
  }

  return (
    <div className="flex w-full flex-col gap-4 pb-32 pt-4">
      <EditProfile
        editProfileForm={editProfileForm}
        editPassword={editPassword}
        onSubmit={onSubmit}
        updatePassword={changePasswordMutation}
        isDirty={isDirty}
        setIsDirty={setIsDirty}
        user={student}
        role="STUDENT"
        updateAuth={
          updateAuth
            ? (auth) => {
                updateAuth(auth as UserAuthStateAuthenticated)
              }
            : undefined
        }
      />

      <div className="flex flex-col">
        <h1 className="text-2xl font-extrabold text-gray-950">Crédits</h1>
        <div>
          <span className="capitalize">{student.firstName}</span> possède{' '}
          {childrenTokenInfo?.balance ?? 0} crédit(s).
        </div>
      </div>

      <h1 className="text-2xl font-extrabold text-gray-950">Cours</h1>
      <div className="flex flex-col gap-4 md:flex-row">
        {/*<div className="flex grow flex-col gap-2">
          <p className="text-sm font-medium text-gray-700">Classe</p>
          <RadioGroup className="flex gap-4" value={checkedGrade ?? undefined}>
            {grade.map((grade) => (
              <div className="flex items-center gap-2" key={grade}>
                <RadioGroupItem
                  value={grade}
                  onClick={() => {
                    setCheckedGrade(grade)
                    setIsDirty(true)
                  }}
                />
                <p className="text-sm font-medium text-gray-700">{GradeEnum[grade]}</p>
              </div>
            ))}
          </RadioGroup>
        </div>*/}
        <div className="flex grow flex-col items-start gap-2 self-stretch">
          <p className="text-sm font-medium text-gray-700">
            Quelle(s) matière(s) souhaitez-vous étudier ?
          </p>
          <div className="flex items-start gap-4 self-stretch">
            {subject.map((subject) => (
              <div className="flex items-center gap-2" key={subject}>
                <Checkbox
                  checked={interestedIn.includes(subject)}
                  onClick={() => {
                    setInterestedIn((prevState) =>
                      prevState.includes(subject)
                        ? prevState.filter((s) => s !== subject)
                        : [...prevState, subject]
                    )
                    setIsDirty(true)
                  }}
                />
                <p className="text-sm font-medium text-gray-700">{SubjectEnum[subject]}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
